// Section 22 explainability: a number with its explain() factor list, as a disclosure that works
// with the keyboard (Enter or Space on the summary) and shows its focus ring. Props only.
import type { Factor } from '../../sim/api';
import { buildText } from '../text/en';

interface Props {
  /** The number as shown. */
  value: string;
  factors: readonly Factor[];
  /** What the number is, for screen readers ("Build points for Granary"). */
  label: string;
}

function trim(x: number): string {
  return (Math.round(x * 100) / 100).toString();
}

function effect(f: Factor): string {
  switch (f.op) {
    case 'base':
      return trim(f.value);
    case 'mul':
      return `x${trim(f.value)}`;
    case 'add':
      return f.value >= 0 ? `+${trim(f.value)}` : trim(f.value);
    case 'set':
      return `= ${trim(f.value)}`;
    case 'min':
      return `at most ${trim(f.value)}`;
    case 'max':
      return `at least ${trim(f.value)}`;
  }
}

export function Explain({ value, factors, label }: Props) {
  return (
    <details class="explain">
      <summary class="num" aria-label={`${label}: ${value}. ${buildText.explain}`}>
        {value}
      </summary>
      <table class="explain__factors">
        <caption class="visually-hidden">{label}</caption>
        <tbody>
          {factors.map((f, i) => (
            <tr key={i}>
              <th scope="row">{f.op === 'base' ? buildText.base : (f.note ?? f.source)}</th>
              <td class="num">{effect(f)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
